import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { Cursor } from '../../../../../../lib/classes/jsPdfCursor';
import { formatDate, formatMoney } from '../../../../../../lib/functions/utils';

function getShortString(string: string, maxLength: number = 25) {
  return string.length > maxLength
    ? string.substring(0, maxLength) + '...'
    : string;
}

export const getImageDimensionsFromBase64 = (
  base64: string,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.src = base64;
  });
};

export const generateSimulationCreditPDF = async (
  data: any,
  userName: string,
  imageBase64: string,
  payouts?: any,
) => {
  const doc = new jsPDF();

  const addTitle = async () => {
    const imgX = 10;
    const imgY = 5;
    const imgHeight = 15;

    const { width: naturalWidth, height: naturalHeight } =
      await getImageDimensionsFromBase64(imageBase64);
    const aspectRatio = naturalWidth / naturalHeight;
    const imgWidth = imgHeight * aspectRatio;
    doc.addImage(imageBase64 as any, 'PNG', imgX, imgY, imgWidth, imgHeight);

    doc.setFontSize(16);
    const title = data.id
      ? `Detalle Solicitud ${data.id}`
      : 'Simulación Crédito';
    doc.text(title, 100, 15, { align: 'center' });

    doc.setFontSize(10);
    doc.text(`Fecha: ${new Date().toLocaleString()}`, 150, 10);
    doc.text(`Ejecutivo: ${userName}`, 150, 15);
  };

  const addSummary = (c: Cursor) => {
    c.writeTitle('Datos');

    c.writeLine('Rut Acreedor', true, 'col11');
    c.writeLine(`: ${data.businessIdentifier}`, false, 'col12');
    c.writeLine('Tipo de crédito', true, 'col21');
    c.writeLineAndNewLine(`: ${data.creditType}`, false, 'col22');

    c.writeLine('Nombre Acreedor', true, 'col11');
    c.writeLine(`: ${getShortString(data.businessName || '')}`, false, 'col12');
    c.writeLine('Tipo de interés', true, 'col21');
    c.writeLineAndNewLine(
      `: ${data.interestType || 'Interés Simple'}`,
      false,
      'col22',
    );

    c.newLine();
    c.writeTitle('Fechas');

    c.writeLine('Fecha inicio', true, 'col11');
    c.writeLine(`: ${data.startDate}`, false, 'col12');
    c.writeLine('Meses', true, 'col21');
    c.writeLineAndNewLine(`: ${data.months}`, false, 'col22');

    c.writeLine('Fecha primer pago', true, 'col11');
    c.writeLine(`: ${data.firstPaymentDate}`, false, 'col12');
    c.writeLine('Total de pagos', true, 'col21');
    c.writeLineAndNewLine(`: ${data.totalPayments}`, false, 'col22');

    c.writeLine('Fecha último pago', true, 'col11');
    c.writeLine(`: ${data.lastPaymentDate}`, false, 'col12');
    c.writeLine('Día de pago', true, 'col21');
    c.writeLineAndNewLine(`: ${data.paymentDay}`, false, 'col22');

    c.newLine();
    c.writeTitle('Montos y tasas');

    // Create pairs of fields to display side by side
    const fieldPairs = [];

    // First column fields
    if (data.showAmount) {
      fieldPairs.push(['Monto', formatMoney(data.totalAmount)]);
    }
    if (data.showInsurance) {
      fieldPairs.push(['Gasto operacional', formatMoney(data.insuranceAmount)]);
    }
    if (data.showOperationExpenses) {
      fieldPairs.push(['Comisión', formatMoney(data.operationAmount)]);
    }
    if (data.showOperationExpenses) {
      fieldPairs.push(['IVA Comisión', formatMoney(data.ivaCreditAmount)]);
    }

    if (data.showTotalCredit) {
      fieldPairs.push(['Total crédito', formatMoney(data.totalLoanAmount)]);
    }
    if (data.showTotalCredit) {
      fieldPairs.push(['Interés total', formatMoney(data.totalInterest)]);
    }
    if (data.showCreditCost) {
      fieldPairs.push(['Costo del crédito', formatMoney(data.totalCost)]);
    }

    // Second column fields
    if (data.showMonthlyInterest) {
      fieldPairs.push(['Interés mensual', `${data.rate.toFixed(2)}%`]);
    }
    if (data.showAnnualInterest) {
      fieldPairs.push(['Interés anual', `${data.anualRate.toFixed(2)}%`]);
    }
    if (data.showArrearInterest) {
      fieldPairs.push(['Interés mora', `${data.arrearRate.toFixed(2)}%`]);
    }
    if (data.showAnnualArrearInterest) {
      fieldPairs.push([
        'Interés mora anual',
        `${data.anualArrearRate.toFixed(2)}%`,
      ]);
    }
    if (data.showCAE) {
      fieldPairs.push(['CAE', `${data.cae.toFixed(2)}%`]);
    }
    if (data.documentsToApply && data.documentsToApply.length > 0) {
      fieldPairs.push(['Docs. aplicados', `${data.documentsToApply.length}`]);
    }
    if (data.documentsToApply && data.documentsToApply.length > 0) {
      fieldPairs.push(['Monto aplicado', `${formatMoney(data.amountToApply)}`]);
    }

    // Display fields in pairs (left column, right column)
    for (let i = 0; i < fieldPairs.length; i += 2) {
      const leftField = fieldPairs[i];
      const rightField = fieldPairs[i + 1];

      if (leftField) {
        c.writeLine(leftField[0], true, 'col11');
        c.writeLine(`: ${leftField[1]}`, false, 'col12');
      } else {
        c.writeLine('', true, 'col11');
        c.writeLine('', false, 'col12');
      }

      if (rightField) {
        c.writeLine(rightField[0], true, 'col21');
        c.writeLineAndNewLine(`: ${rightField[1]}`, false, 'col22');
      } else {
        c.writeLine('', true, 'col21');
        c.writeLineAndNewLine('', false, 'col22');
      }
    }

    c.newLine();
  };

  const addWarrantiesTable = (c: Cursor) => {
    c.writeTitle('Garantías');
    c.writeTable(
      ['Tipo', 'Descripción', 'Valor'],
      data.warranties.map((warranty: any) => [
        warranty.type,
        warranty.description,
        formatMoney(warranty.value),
      ]),
    );
  };

  const addAmortizationTable = (c: Cursor) => {
    c.newLine();
    c.writeTitle('Cuadro de pagos');
    c.writeTable(
      [
        'N° Cuota',
        'Fecha Pago',
        'Saldo',
        'Cuota',
        'Interés',
        'Capital',
        'Amortización',
      ],
      data.amortizationSchedule.map((payment: any) => [
        payment.month,
        formatDate(payment.paymentDate, 'dd-mm-yyyy'),
        formatMoney(payment.balance),
        formatMoney(payment.amount),
        formatMoney(payment.interest),
        formatMoney(payment.principal),
        formatMoney(payment.totalAmortization),
      ]),
    );
  };

  const addDocumentsTable = (c: Cursor) => {
    if (!data.documents || data.documents.length === 0) return;
    c.newLine();
    c.writeTitle('Documentos');
    c.writeTable(
      ['Folio', 'Monto', 'Fecha'],
      data.documents.map((doc: any) => [
        doc.folio || doc.id || doc.documentId || 'N/A',
        formatMoney(doc.amount || 0),
        doc.paymentDate ? formatDate(doc.paymentDate, 'dd-mm-yyyy') : 'N/A',
      ]),
    );
  };

  const addDocumentsToApplyTable = (c: Cursor) => {
    if (!data.documentsToApply || data.documentsToApply.length === 0) return;
    doc.addPage();
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Documentos aplicados', 10, 20);
    doc.setFont('helvetica', 'normal');
    c.writeTable(
      ['Folio', 'Tipo', 'Monto', 'Monto Aplicado'],
      data.documentsToApply.map((doc: any) => [
        doc.folio || doc.id || doc.documentId || 'N/A',
        doc.type || 'N/A',
        formatMoney(doc.amount || 0),
        formatMoney(doc.amountToConciliate || 0),
      ]),
      { startX: 10, startY: 30 }, // Usar un objeto para `customProps`
    );
  };

  const addPayoutsTable = (c: Cursor) => {
    if (!payouts || !Array.isArray(payouts.payments)) return;
    doc.addPage();

    // Add title manually
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Pagos Realizados', 10, 20);

    // Use autoTable directly for better control
    autoTable(doc, {
      startY: 25,
      head: [
        [
          'Banco Origen',
          'N° Cuenta Origen',
          'Banco Destino',
          'N° Cuenta Destino',
          'RUT Destino',
          'Tipo Cuenta Destino',
          'Titular Cuenta Destino',
          'Giro',
          'Monto',
        ],
      ],
      body:
        payouts.payments.length > 0
          ? payouts.payments.map((p: any) => [
              p.bankAccountOriginName,
              p.bankAccountOriginNumber,
              p.bankAccountDestinationName,
              p.bankAccountDestinationNumber,
              p.bankAccountDestinationHolderIdentifier,
              p.bankAccountDestinationAccountType,
              p.bankAccountDestinationHolderName,
              p.executedAt ? formatDate(p.executedAt) : '',
              formatMoney(p.amount),
            ])
          : [
              [
                {
                  content: 'No hay pagos realizados',
                  colSpan: 9,
                  styles: { halign: 'center', fontStyle: 'italic' },
                },
              ],
            ],
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 20 },
        2: { cellWidth: 20 },
        3: { cellWidth: 20 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
        6: { cellWidth: 25 },
        7: { cellWidth: 20 },
        8: { cellWidth: 20 },
      },
    });
  };

  const c = new Cursor(doc);
  await addTitle();
  addSummary(c);
  if (data.warranties.length > 0 && data.showWarranties) {
    addWarrantiesTable(c);
  }
  if (data.amortizationSchedule.length > 0 && data.showPaymentTable) {
    addAmortizationTable(c);
  }
  if (data.documents && data.documents.length > 0) {
    addDocumentsTable(c);
  }
  if (data.documentsToApply && data.documentsToApply.length > 0) {
    addDocumentsToApplyTable(c);
  }
  if (payouts) {
    addPayoutsTable(c);
  }

  doc.save(`SIMULACION-CREDITO-${data.businessName}.pdf`);
};
